import React, { useEffect, useRef, useMemo } from 'react';
import AppStyle from '@atlassian/jira-common-components-app-style/src/index.tsx';
import { FlagsDispatcher } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import AppBase from '@atlassian/jira-issue-view-app-base/src/index.tsx';
import { statusNone } from '@atlassian/jira-issue-view-common-types/src/issue-type.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { getApplicationForProject } from '@atlassian/jira-shared-types/src/application.tsx';
import { getEdition } from '@atlassian/jira-shared-types/src/edition.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { CHILD_ISSUES_PANEL_TYPE } from './common/constants.tsx';
import type { Props } from './common/types.tsx';
import { transformIssueType } from './common/utils/transform-issue-type/index.tsx';
import flagMapper from './flags/index.tsx';
import rootEpic from './ops/index.tsx';
import {
	setContext,
	setOnChange,
	requiresFullCreateCheckRequest,
} from './state/context/actions.tsx';
import { setEntities, fetchDetailsForIssuesRequest } from './state/entities/actions.tsx';
import createStore from './state/index.tsx';
import { setFetchIssueTypesStatus, showInlineCreate } from './state/ui/actions.tsx';
import View from './view/index.tsx';

const ChildIssuesPanelBaseOld = (props: Props) => {
	const {
		associatedIssuesContextActions,
		appEditions,
		baseUrl,
		childIssuesPanelType,
		customFieldIdRank,
		epicLinkFieldKey,
		fetchIssueTypesStatus,
		fieldConfigActions,
		fieldValuesActions,
		filterSubtasks,
		isIssueViewComplete = false,
		isSimplifiedProject,
		issueHierarchyLevel,
		issues = [],
		issueTypes = [],
		locale,
		messages,
		onChange,
		onEditAssociatedIssue,
		onNavigateToNewIssue,
		parentId,
		parentIssueTypeId,
		parentKey,
		projectId,
		projectType,
		quickAddClickCount,
		supportsIssueCreation,
		triggeredViaHotKey,
		user,
	} = props;

	const store = useMemo(() => {
		return createStore(
			rootEpic(associatedIssuesContextActions, fieldValuesActions, fieldConfigActions),
			parentKey,
			childIssuesPanelType,
		);
	}, [
		associatedIssuesContextActions,
		childIssuesPanelType,
		fieldConfigActions,
		fieldValuesActions,
		parentKey,
	]);

	const hasMounted = useRef(false);
	const prevQuickAddClickCount = useRef(quickAddClickCount);

	useEffect(() => {
		// Prevent concurrent mode rerunning this useEffect and making multiple api calls
		if (hasMounted.current) {
			return;
		}

		// TODO: Remove on cleanup of jira-issue-view-child-issues-panel-performance
		const oldContext = {
			baseUrl,
			locale,
			user,
			projectId,
			parentId,
			parentKey,
			parentIssueTypeId,
			customFieldIdRank,
			epicLinkFieldKey,
			supportsIssueCreation,
			messages,
			onNavigateToNewIssue,
			onChange,
			childIssuesPanelType,
			isIssueViewComplete,
			projectType,
			application: projectType !== null ? getApplicationForProject(projectType) : null,
			edition:
				projectType !== null
					? getEdition(getApplicationForProject(projectType), appEditions)
					: null,
			// Optimistically assume full create not needed
			requiresFullCreate: false,
			// TODO: Remove on jira-issue-view-child-issues-panel-merge-subtasks cleanup
			filterSubtasks,
			// if the endpoint doesn't return data quick enough, optimistically assume full create not needed
			requiresFullCreateByIssueType: {},
			onEditAssociatedIssue,
			isSimplifiedProject,
		};

		const newContext = {
			baseUrl,
			user,
			projectId,
			parentId,
			parentKey,
			parentIssueTypeId,
			epicLinkFieldKey,
			supportsIssueCreation,
			messages,
			onChange,
			childIssuesPanelType,
			projectType,
			application: projectType !== null ? getApplicationForProject(projectType) : null,
			edition:
				projectType !== null
					? getEdition(getApplicationForProject(projectType), appEditions)
					: null,
			// Optimistically assume full create not needed
			requiresFullCreate: false,
			// TODO: Remove on jira-issue-view-child-issues-panel-merge-subtasks cleanup
			filterSubtasks,
			// if the endpoint doesn't return data quick enough, optimistically assume full create not needed
			requiresFullCreateByIssueType: {},
			isSimplifiedProject,
		};

		const context = fg('jira-issue-view-child-issues-panel-performance') ? newContext : oldContext;
		store.dispatch(setContext(context));

		store.dispatch(requiresFullCreateCheckRequest());

		store.dispatch(
			setEntities({
				issueTypes: issueTypes.map((issueType) => transformIssueType(issueType, epicLinkFieldKey)),
				issues,
				issueHierarchyLevel,
			}),
		);

		if (childIssuesPanelType !== CHILD_ISSUES_PANEL_TYPE.CLASSIC_SUBTASKS && issues.length > 0) {
			store.dispatch(fetchDetailsForIssuesRequest());
		}

		if (fetchIssueTypesStatus) {
			store.dispatch(setFetchIssueTypesStatus(fetchIssueTypesStatus));
		}

		if (quickAddClickCount > 0) {
			if (expVal('quick_actions_m3_experiment', 'hotKeysEnabled', false)) {
				store.dispatch(showInlineCreate({ triggeredViaHotKey }));
			} else {
				store.dispatch(showInlineCreate());
			}
		}

		hasMounted.current = true;
	}, [
		appEditions,
		baseUrl,
		childIssuesPanelType,
		customFieldIdRank,
		epicLinkFieldKey,
		fetchIssueTypesStatus,
		filterSubtasks,
		isIssueViewComplete,
		isSimplifiedProject,
		issueHierarchyLevel,
		issueTypes,
		issues,
		locale,
		messages,
		onChange,
		onEditAssociatedIssue,
		onNavigateToNewIssue,
		parentId,
		parentIssueTypeId,
		parentKey,
		projectId,
		projectType,
		props.appEditions,
		quickAddClickCount,
		store,
		supportsIssueCreation,
		triggeredViaHotKey,
		user,
	]);

	useEffect(() => {
		if (quickAddClickCount > prevQuickAddClickCount.current) {
			if (expVal('quick_actions_m3_experiment', 'hotKeysEnabled', false)) {
				store.dispatch(showInlineCreate({ triggeredViaHotKey }));
			} else {
				store.dispatch(showInlineCreate());
			}
		}
		prevQuickAddClickCount.current = quickAddClickCount;
	}, [quickAddClickCount, triggeredViaHotKey, store, props]);

	useEffect(() => {
		if (fetchIssueTypesStatus) {
			store.dispatch(setFetchIssueTypesStatus(fetchIssueTypesStatus || statusNone));
		}
	}, [fetchIssueTypesStatus, store]);

	useEffect(() => {
		if (onChange) {
			store.dispatch(setOnChange(onChange));
		}
	}, [onChange, store]);

	useEffect(() => {
		if (issues.length || fetchIssueTypesStatus) {
			store.dispatch(
				setEntities({
					issueTypes: issueTypes.map((issueType) =>
						transformIssueType(issueType, epicLinkFieldKey),
					),
					issues,
					issueHierarchyLevel,
				}),
			);
		}
	}, [issues, fetchIssueTypesStatus, issueTypes, epicLinkFieldKey, issueHierarchyLevel, store]);

	return (
		<AppBase store={store}>
			<ContextualAnalyticsData
				sourceName="childIssuesPanel"
				sourceType={SCREEN}
				attributes={{
					childIssuesPanelType,
					issueHierarchyLevel: issueHierarchyLevel - 1,
					analyticsSource: `issue-view (${props.analyticsSource})`,
					location: 'jiraChildIssues',
				}}
			>
				<AppStyle>
					<View
						// @ts-expect-error - onAddChildClick is AddChildEventHandler | undefined
						onAddChildClick={props.onAddChildClick}
						onIssueBreakdownClick={props.onIssueBreakdownClick}
						sourceName="childIssuesPanel"
						childIssuesLimitUrl={props.childIssuesLimitUrl}
						hasExceededIssuesLimitAfterLoad={props.hasExceededIssuesLimitAfterLoad}
						totalChildIssueCount={props.totalChildIssueCount}
						issueCreateSessionId={props.issueCreateSessionId}
						rootRelayFragment={props.rootRelayFragment}
						hasMultipleChildPanels={props.hasMultipleChildPanels}
					/>
					<FlagsDispatcher mapper={flagMapper} />
				</AppStyle>
			</ContextualAnalyticsData>
		</AppBase>
	);
};

export default ChildIssuesPanelBaseOld;
